import React from 'react';
import { Box } from '@mui/material';
import { GlitchParams } from '../../constants/bannerEffect';

/**
 * ヘッダー画像のグリッチ。
 *
 * 横帯を切り出して、たまに横へ飛ばすだけ。**JSは1行も走らない**（初期化時の位相決めのみ）。
 * 以前ここは WebGL で歪ませていたが、47万pxを毎フレーム描くのが割に合わず撤去した。
 * グリッチは滑らかさが要らない（むしろ不連続なほうが正しい）ので、CSS で足りる。
 *
 * 安く保つための条件は2つ。守らないと意味がなくなる。
 * - 動かすのは transform と opacity だけ。位置やクリップをアニメーションさせない
 * - 1周期のほとんどを静止に使う。乱れるのはバーストの間だけ
 *   （steps(1) で補間もしないので、その間コンポジタは何もしない）
 *
 * 【規則的に見せないための仕掛け】
 * CSSアニメーションは同じ内容を永久に繰り返すので、素直に組むと拍になる。
 * - `rate` で**割り切れない長さの周期**を各層に与える。ずれ続けるので、
 *   全部が揃って大きく崩れる回と、一部しか動かない回が不規則に混ざる
 * - 開始位相を読み込みごとに乱数で散らす（同じ回でも見え方が変わる）
 * - バーストの形を3種類用意して層ごとに変える
 * **rate を揃えたり、位相を固定したりしないこと。** 途端にパルスに戻る。
 */

// top / height はバナー高さに対する％。x/y は基準のずれ px。
// rate は周期の倍率で、互いに割り切れない値にしてある。shape はバーストの形。
const BANDS = [
  { top: 6, height: 4, x: -38, y: 0, rate: 0.83, shape: 0 },
  { top: 13, height: 12, x: 24, y: 0, rate: 1.31, shape: 1 },
  { top: 28, height: 3, x: 54, y: 0, rate: 0.71, shape: 2 },
  { top: 38, height: 19, x: -18, y: 2, rate: 1.63, shape: 0 },
  { top: 60, height: 5, x: 44, y: 0, rate: 0.97, shape: 2 },
  { top: 68, height: 10, x: -30, y: -2, rate: 1.19, shape: 1 },
  { top: 83, height: 3, x: 34, y: 0, rate: 0.59, shape: 2 },
];

const at = (x: number, y = 0) => ({ transform: `translate3d(${x}px,${y}px,0)` });

// バーストの形。どれも大半は静止で、乱れる区間だけ値が動く。
const SHAPES: Array<(x: number, y: number) => Record<string, object>> = [
  // 2連。短く弾いて戻る
  (x, y) => ({
    '0%, 88%': at(0),
    '88.5%': at(x, y),
    '89.3%': at(0),
    '90.1%': at(-x * 0.55),
    '90.9%, 100%': at(0),
  }),
  // 長め。大きい帯向けにゆっくり滞留させる
  (x, y) => ({
    '0%, 74%': at(0),
    '74.5%': at(x * 0.7, y),
    '76.2%': at(0),
    '91%': at(-x),
    '92.4%': at(x * 0.4, -y),
    '93.1%, 100%': at(0),
  }),
  // 細かく刻む。細い帯向け
  (x, y) => ({
    '0%, 46%': at(0),
    '46.4%': at(x, y),
    '47%': at(0),
    '86%': at(0),
    '86.4%': at(-x * 0.8),
    '87%': at(x * 0.5),
    '87.6%': at(0),
    '93%': at(x * 0.9, y),
    '93.6%, 100%': at(0),
  }),
];

// 色ずれ。同じ絵を色相をずらして重ね、バーストの間だけ見せる。
// mix-blend-mode は使わない（合成だけで完結できず再ペイントになるため）。
const fringeKeyframes = (x: number) => ({
  '0%, 62%': { opacity: 0, ...at(0) },
  '62.5%': { opacity: 0.5, ...at(x) },
  '64%': { opacity: 0, ...at(0) },
  '90%': { opacity: 0.55, ...at(-x) },
  '91.2%': { opacity: 0.45, ...at(x * 1.4) },
  '92.4%': { opacity: 0.5, ...at(-x * 0.6) },
  '93.4%, 100%': { opacity: 0, ...at(0) },
});

// 色ずれの周期倍率。帯の rate ともぶつからない値にすること
const FRINGE_RATE = 1.43;

const BannerGlitch: React.FC<{
  /** 下地と同じ background 文字列。ズレると帯だけ絵が食い違う */
  background: string;
  params: GlitchParams;
}> = ({ background, params }) => {
  // 開始位相。読み込みごとに散らす（固定すると毎回同じ見え方になる）
  const phases = React.useMemo(
    () => Array.from({ length: BANDS.length + 1 }, () => Math.random()),
    []
  );

  const anim = (name: string, rate: number, phase: number) => {
    const duration = params.cycle * rate;
    return {
      animation: `${name} ${duration.toFixed(2)}s steps(1, end) infinite`,
      // 負の遅延で途中から始める。全層が同時に開始しないようにするため
      animationDelay: `${-(phase * duration).toFixed(2)}s`,
      '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
    };
  };

  return (
    <>
      {/* 色ずれ。全面だがバーストの間しか見えない */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background,
          filter: 'hue-rotate(150deg) saturate(2.4)',
          opacity: 0,
          willChange: 'opacity, transform',
          '@keyframes bannerGlitchFringe': fringeKeyframes(12 * params.shift),
          ...anim('bannerGlitchFringe', FRINGE_RATE, phases[BANDS.length]),
        }}
      />

      {BANDS.map((band, i) => {
        const name = `bannerGlitchBand${i}`;
        return (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${band.top}%`,
              height: `${band.height}%`,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {/* 内側をバナーと同じ寸法・同じ位置に戻すことで、下地と絵が一致する。
                帯の高さを基準にした％なので、10000/height で元の高さに戻る。 */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                width: '100%',
                top: `${-(band.top / band.height) * 100}%`,
                height: `${10000 / band.height}%`,
                background,
                [`@keyframes ${name}`]: SHAPES[band.shape](band.x * params.shift, band.y),
                ...anim(name, band.rate, phases[i]),
              }}
            />
          </Box>
        );
      })}
    </>
  );
};

export default BannerGlitch;
